import { Link } from 'react-router-dom';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import SEO from '../../components/feature/SEO';
import ServiciosGrid from './components/ServiciosGrid';
import MarcasProceso from './components/MarcasProceso';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const serviciosJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AutoRepair',
    name: 'Taller Mecánico Kevin Alberto – Servicios',
    url: `${siteUrl}/servicios`,
    telephone: '+34666365922',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'C. Guadalimar, 7',
      addressLocality: 'Ronda',
      postalCode: '29400',
      addressRegion: 'Málaga',
      addressCountry: 'ES',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Servicios de taller mecánico en Ronda',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Revisión periódica',
            description: 'Revisión completa del vehículo según normativa del fabricante.',
          },
          priceSpecification: { '@type': 'PriceSpecification', price: '49', priceCurrency: 'EUR' },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Diagnóstico electrónico',
            description: 'Lectura de errores y diagnosis avanzada con equipos de última generación.',
          },
          priceSpecification: { '@type': 'PriceSpecification', price: '29', priceCurrency: 'EUR' },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Frenos y suspensión',
            description: 'Revisión, reparación y sustitución de frenos y suspensión.',
          },
          priceSpecification: { '@type': 'PriceSpecification', price: '79', priceCurrency: 'EUR' },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Chapa y pintura',
            description: 'Reparación de carrocería, abolladuras y pintura profesional.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Climatización',
            description: 'Carga de gas, reparación y mantenimiento del sistema de aire acondicionado.',
          },
          priceSpecification: { '@type': 'PriceSpecification', price: '59', priceCurrency: 'EUR' },
        },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Servicios – Taller Mecánico Kevin Alberto Ronda',
    url: `${siteUrl}/servicios`,
    description:
      'Mecánica completa, chapa, pintura y diagnóstico electrónico en Ronda, Málaga. Presupuesto siempre antes de empezar.',
    inLanguage: 'es',
    isPartOf: { '@type': 'WebSite', url: siteUrl },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿Qué incluye la revisión periódica en el taller de Ronda?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'La revisión periódica en Taller Mecánico Kevin Alberto incluye comprobación de frenos, niveles de fluidos, neumáticos, iluminación, filtros y diagnosis electrónica del vehículo desde 49 €.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Cuánto cuesta un diagnóstico electrónico en Ronda, Málaga?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'El diagnóstico electrónico en nuestro taller de Ronda cuesta desde 29 €. Disponemos de equipos de última generación compatibles con todas las marcas de vehículos.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Hacéis chapa y pintura en Ronda?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí, disponemos de servicio completo de chapa y pintura en Ronda, Málaga. Reparamos abolladuras, rayadas y daños de carrocería con pintura profesional al tono original del vehículo.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Reparáis frenos y suspensión en el taller de Ronda?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí, ofrecemos revisión, reparación y sustitución de frenos, amortiguadores y suspensión desde 79 €. Trabajamos con piezas de calidad para garantizar tu seguridad en la carretera.',
        },
      },
    ],
  },
];

const ServiciosPage = () => (
  <PageLayout>
    <SEO
      title="Servicios de Mecánica, Diagnosis y Chapa en Ronda Málaga | Taller Kevin Alberto"
      description="Mecánica completa, chapa, pintura y diagnóstico electrónico para cualquier coche o moto en Ronda, Málaga. Presupuesto sin compromiso. Taller Kevin Alberto."
      keywords="servicios mecánica Ronda, diagnosis electrónica Ronda, chapa pintura Ronda Málaga, frenos suspensión Ronda"
      canonical="/servicios"
      jsonLd={serviciosJsonLd}
    />
    <PageHero
      tag="Lo que hacemos"
      title="Nuestros"
      titleAccent="Servicios"
      subtitle="Mecánica completa, chapa, pintura y diagnóstico electrónico para cualquier coche o moto. Presupuesto siempre antes de empezar."
      breadcrumb="Servicios"
      bgImage="https://public.readdy.ai/ai/img_res/a2799d66b4c3de03c2bf1bc9f4a46658.jpg"
    />

    {/* Calculadora CTA strip */}
    <div className="bg-yellow-400 py-4 px-6">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-black/15">
            <i className="ri-calculator-line text-black text-base" />
          </div>
          <div>
            <p className="text-black text-xs font-bold uppercase tracking-widest leading-none mb-0.5">¿Cuánto costará?</p>
            <p className="text-black/70 text-[11px] leading-none">Calcula tu presupuesto orientativo en segundos, gratis y sin compromiso</p>
          </div>
        </div>
        <Link
          to="/calculadora"
          className="flex-shrink-0 inline-flex items-center gap-2 bg-black text-yellow-400 text-[11px] font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-zinc-900 transition-colors duration-300 cursor-pointer whitespace-nowrap"
        >
          Usar calculadora <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </div>

    <ServiciosGrid />
    <MarcasProceso />
  </PageLayout>
);

export default ServiciosPage;
